import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";

// Approvals history — terminal-state proposals.
//
// Loads `GET /v2/admin/bff/approvals?status=executed` by default.
// Operator can also view rejected / expired / cancelled / approved
// (not-yet-executed) via the status filter. Each row shows the
// on-chain breadcrumb (tx hash + block + BaseScan link) when
// available — populated by BE PR #136 / migration 0044 on the
// success path of `/withdraw-treasury` and `/withdraw-affiliate`.

interface BffProposalView {
  id: string;
  actionType: string;
  actionDigest: string;
  payloadHash: string;
  payload: unknown;
  thresholdRequired: number;
  status: "proposed" | "approved" | "rejected" | "expired" | "executed" | "cancelled";
  proposedByOwnerId: string;
  expiresAt: string;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  executedAt: string | null;
  cancelledAt: string | null;
  executedTxHash: string | null;
  executedBlockNumber: number | null;
  executedByWalletAddress: string | null;
}

interface BffApprovalsListResponse {
  ok: boolean;
  status: string | null;
  proposals: BffProposalView[];
  sessionOwnerIdentityId: string;
  sessionWalletAddress: string;
  requestId: string;
}

type HistoryFilter = "executed" | "rejected" | "expired" | "cancelled" | "approved";

function summariseActionType(actionType: string): string {
  switch (actionType) {
    case "money_movement":
      return "Money movement";
    case "high_config":
      return "Config (high)";
    case "low_config":
      return "Config (low)";
    case "production_deploy":
      return "Production deploy";
    case "read":
      return "Read";
    default:
      return actionType;
  }
}

function summarisePayload(p: unknown): string {
  if (p === null || typeof p !== "object") return "";
  const o = p as Record<string, unknown>;
  if (o["action"] === "withdrawTreasury") {
    const amt =
      typeof o["amountHumanReadable"] === "string"
        ? (o["amountHumanReadable"] as string)
        : typeof o["amountWei"] === "string"
          ? `${o["amountWei"] as string} wei`
          : "?";
    const dst = typeof o["destination"] === "string" ? (o["destination"] as string) : "?";
    return `withdrawTreasury ${amt} → ${dst}`;
  }
  if (typeof o["action"] === "string") return String(o["action"]);
  return "";
}

function ApprovalsHistoryInner() {
  const [data, setData] = useState<BffApprovalsListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<HistoryFilter>("executed");

  const load = useCallback(async (status: HistoryFilter) => {
    setError(null);
    setLoading(true);
    try {
      const j = await apiCall<BffApprovalsListResponse>("/v2/admin/bff/approvals", {
        query: { status },
      });
      setData(j);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError("Route /v2/admin/bff/approvals not registered on BE.");
      } else if (e instanceof ApiHttpError) {
        setError(`HTTP ${e.status} from /v2/admin/bff/approvals.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [load, filter]);

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
        Terminal-state proposals. On-chain tx hashes shown for{" "}
        <code>executed</code> proposals (post BE PR #136 / migration 0044).
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <label htmlFor="history-filter" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          Status:
        </label>
        <select
          id="history-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as HistoryFilter)}
          style={{ padding: "4px 8px", fontSize: 13 }}
        >
          <option value="executed">Executed</option>
          <option value="approved">Approved (pending execute)</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button
          type="button"
          onClick={() => void load(filter)}
          disabled={loading}
          style={{ padding: "4px 12px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
        <Link to="/approvals" style={{ fontSize: 12, marginLeft: "auto", color: "var(--accent, #2c7be5)" }}>
          ← Active approvals
        </Link>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            border: "1px solid #d23f3f",
            background: "rgba(210, 63, 63, 0.06)",
            color: "#d23f3f",
            margin: "12px 0",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {data && data.proposals.length === 0 && (
        <p style={{ padding: 16, fontSize: 13, color: "var(--fg-dim)" }}>
          No proposals with status "{filter}".
        </p>
      )}

      {data && data.proposals.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
              <th style={{ padding: "8px 4px" }}>Proposal</th>
              <th style={{ padding: "8px 4px" }}>Action</th>
              <th style={{ padding: "8px 4px" }}>Executed at</th>
              <th style={{ padding: "8px 4px" }}>Tx</th>
            </tr>
          </thead>
          <tbody>
            {data.proposals.map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}>
                <td style={{ padding: "8px 4px", fontFamily: "monospace" }}>
                  <Link to={`/approvals/${p.id}`} style={{ color: "var(--accent, #2c7be5)" }}>
                    {p.id}
                  </Link>
                  <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 2 }}>
                    by <code>{p.proposedByOwnerId}</code>
                  </div>
                </td>
                <td style={{ padding: "8px 4px" }}>
                  <div>{summariseActionType(p.actionType)}</div>
                  <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 2 }}>
                    {summarisePayload(p.payload)}
                  </div>
                </td>
                <td style={{ padding: "8px 4px", fontSize: 11 }}>
                  <code>{p.executedAt ?? "—"}</code>
                  {p.executedBlockNumber !== null && (
                    <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 2 }}>
                      block <code>{p.executedBlockNumber}</code>
                    </div>
                  )}
                </td>
                <td style={{ padding: "8px 4px", fontSize: 11, wordBreak: "break-all" }}>
                  {p.executedTxHash ? (
                    <>
                      <code>
                        {p.executedTxHash.slice(0, 10)}…{p.executedTxHash.slice(-8)}
                      </code>
                      <div style={{ marginTop: 2 }}>
                        <a
                          href={`https://sepolia.basescan.org/tx/${p.executedTxHash}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "var(--accent, #2c7be5)", fontSize: 11 }}
                        >
                          BaseScan ↗
                        </a>
                      </div>
                    </>
                  ) : (
                    <span style={{ color: "var(--fg-dim)" }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ApprovalsHistory() {
  return (
    <div>
      <PageHeader
        title="Approval history"
        subtitle="Terminal-state proposals. On-chain breadcrumbs (tx + block) shown for executed proposals."
        method="GET"
        path="/v2/admin/bff/approvals?status=executed"
      />
      <AuthRequired>
        <ApprovalsHistoryInner />
      </AuthRequired>
    </div>
  );
}
